import { useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star, ArrowRight } from "lucide-react";
import { useT, useUI, useLang } from "../lib/i18n";
import SEO from "../components/site/SEO";

export default function Pricing() {
  const { content } = useOutletContext();
  const t = useT();
  const ui = useUI();
  const lang = useLang();
  const pricing = content.pricing || {};
  const packs = pricing.packs || [];
  const seo = (content.seo || {}).pricing;

  return (
    <div data-testid="pricing-page">
      <SEO seoData={seo} />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-12 text-center">
        <p className="overline mb-4">{t(pricing.overline)}</p>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-none font-light max-w-4xl mx-auto">{t(pricing.title)}</h1>
        <p className="mt-8 max-w-2xl mx-auto text-[#4A3F35] text-lg">{t(pricing.description)}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {packs.map((p, i) => {
            const popular = p.popular;
            return (
              <motion.div
                key={p.id || i}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className={`relative rounded-3xl p-8 md:p-10 flex flex-col ${popular ? "bg-[#2D2A26] text-[#FDFBF7]" : "bg-[#FDFBF7] border border-[#2D2A26]/10"}`}
                data-testid={`pricing-card-${p.id || i}`}
              >
                {popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-[#C87A5E] text-white px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-semibold shadow-lg">
                    <Star size={12} className="fill-white" /> {lang === "fr" ? "Le plus populaire" : "Most popular"}
                  </div>
                )}
                <div>
                  <h3 className="font-serif text-3xl md:text-4xl">{t(p.name)}</h3>
                  <div className="mt-5 flex items-baseline gap-1">
                    <span className="font-serif text-6xl font-light">{p.price}</span>
                    <span className={`text-2xl ${popular ? "text-[#C87A5E]" : "text-[#C87A5E]"}`}>{p.currency || "€"}</span>
                  </div>
                  <p className={`mt-4 text-sm ${popular ? "text-[#FDFBF7]/70" : "text-[#4A3F35]"}`}>{t(p.description)}</p>
                </div>

                <div className={`mt-6 py-4 my-2 border-y ${popular ? "border-[#FDFBF7]/15" : "border-[#2D2A26]/10"} flex justify-between items-center text-sm`}>
                  <div>
                    <div className={`overline ${popular ? "!text-[#C87A5E]" : ""}`}>{lang === "fr" ? "Durée" : "Duration"}</div>
                    <div className="font-medium mt-1">{t(p.duration)}</div>
                  </div>
                  <div className="text-right">
                    <div className={`overline ${popular ? "!text-[#C87A5E]" : ""}`}>{lang === "fr" ? "Livraison" : "Delivered"}</div>
                    <div className="font-medium mt-1">{t(p.photos)}</div>
                  </div>
                </div>

                <ul className="mt-6 space-y-3 flex-1">
                  {(p.features || []).map((f, j) => (
                    <li key={`${p.id || i}-feature-${j}`} className="flex items-start gap-3 text-sm">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${popular ? "bg-[#C87A5E]" : "bg-[#8A9A86]/15 text-[#8A9A86]"}`}>
                        <Check size={12} className={popular ? "text-white" : ""} />
                      </div>
                      <span className={popular ? "text-[#FDFBF7]/90" : "text-[#4A3F35]"}>{t(f)}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/${lang}/contact`}
                  className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-medium transition ${popular ? "bg-[#C87A5E] text-white hover:bg-[#FDFBF7] hover:text-[#2D2A26]" : "bg-[#2D2A26] text-[#FDFBF7] hover:bg-[#C87A5E]"}`}
                  data-testid={`pricing-cta-${p.id || i}`}
                >
                  {lang === "fr" ? "Choisir ce pack" : "Choose this pack"} <ArrowRight size={14} />
                </Link>
              </motion.div>
            );
          })}
        </div>

        {pricing.footer_note && (
          <p className="mt-12 text-center text-sm text-[#4A3F35]/70 max-w-2xl mx-auto italic">{t(pricing.footer_note)}</p>
        )}
      </section>
    </div>
  );
}
